import { Controller } from "@hotwired/stimulus"
import { parseExifLocation } from "../lib/exif"
import { canvasResize, PHOTO_THUMBNAIL } from "../lib/image_resize"
import { postJson } from "../lib/http"
import { uploadThumbnailsInChunks } from "../lib/thumbnail_backfill"

// Web upload controller for the /library_photos/new page.
// Reads GPS + date from selected files via exifr, uploads metadata to
// /library_photos/batch_create, uploads thumbnails, then navigates to /discoveries.
export default class extends Controller {
  static targets = [
    "dropZone", "fileInput", "scanProgress", "progressBar",
    "progressLabel", "progressCount", "error"
  ]

  static values = {
    batchCreateUrl: String,
    thumbnailsUrl:  String,
    // 0 = public/central library; >0 = upload these photos for that community only.
    communityId:    Number,
    // Where to go after upload — the group page for a community upload, else Home.
    returnUrl:      String
  }

  connect() {
    window.photoCache = window.photoCache || {}
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  openPicker() {
    this.fileInputTarget.click()
  }

  dragover(event) {
    event.preventDefault()
    this.dropZoneTarget.style.borderColor = "#7c3aed"
    this.dropZoneTarget.style.background  = "#faf5ff"
  }

  dragleave() {
    this.dropZoneTarget.style.borderColor = "#d1d5db"
    this.dropZoneTarget.style.background  = "white"
  }

  drop(event) {
    event.preventDefault()
    this.dropZoneTarget.style.borderColor = "#d1d5db"
    this.dropZoneTarget.style.background  = "white"
    this._process(Array.from(event.dataTransfer.files))
  }

  filesSelected() {
    this._process(Array.from(this.fileInputTarget.files))
  }

  // ── Private ──────────────────────────────────────────────────────────────

  async _process(files) {
    if (!files.length) return

    this.dropZoneTarget.style.display   = "none"
    this.scanProgressTarget.style.display = "block"
    this.errorTarget.style.display      = "none"

    this.progressLabelTarget.textContent = "Reading photo metadata…"

    const metadata = []
    const fileByRef = {}

    for (let i = 0; i < files.length; i++) {
      this.progressBarTarget.style.width  = Math.round(((i + 1) / files.length) * 100) + "%"
      this.progressCountTarget.textContent = `${i + 1} of ${files.length}`

      const loc = await parseExifLocation(files[i])
      if (loc) {
        // Include size so distinct photos can't collide on the client_ref —
        // a collision makes the server keep the first photo's coordinates for all
        // of them (see library_photos#batch_create), collapsing matches to "0m".
        const clientRef = "web-" + files[i].name + "-" + files[i].size + "-" + files[i].lastModified
        window.photoCache[clientRef] = URL.createObjectURL(files[i])
        // Keep the File so we can generate its thumbnail + blur after the metadata
        // rows exist (the thumbnails endpoint updates rows by client_ref).
        fileByRef[clientRef] = files[i]

        metadata.push({
          latitude:   loc.latitude,
          longitude:  loc.longitude,
          taken_on:   loc.takenOn,
          taken_at:   loc.takenAt,
          client_ref: clientRef
        })
      }
    }

    if (metadata.length === 0) {
      this.scanProgressTarget.style.display = "none"
      this.dropZoneTarget.style.display     = "block"
      this.errorTarget.textContent = "None of those photos have GPS data. Try photos taken on your phone with location services on."
      this.errorTarget.style.display = "block"
      return
    }

    // Upload metadata (with thumbnails included) in batches
    this.progressLabelTarget.textContent = "Uploading…"
    this.progressBarTarget.style.width   = "0%"
    this.progressCountTarget.textContent = ""

    const BATCH = 25
    for (let i = 0; i < metadata.length; i += BATCH) {
      await postJson(this.batchCreateUrlValue, { metadata: metadata.slice(i, i + BATCH), community_id: this.communityIdValue })
      this.progressBarTarget.style.width = Math.round(((i + BATCH) / metadata.length) * 100) + "%"
    }

    // Now that the rows exist, generate + upload each photo's thumbnail and blur
    // placeholder. This shared helper also runs the on-device NSFW filter, so a
    // flagged photo is marked explicit (and excluded from matching) BEFORE
    // finalize runs — same pipeline the native upload uses.
    this.progressLabelTarget.textContent = "Processing photos…"
    await uploadThumbnailsInChunks(
      Object.keys(fileByRef),
      (ref) => canvasResize(fileByRef[ref], PHOTO_THUMBNAIL)
    )

    // All photos in — run matching once over the complete library (public →
    // global matching; a community → match only within that group). Matching
    // skips any photo the NSFW filter flagged above.
    await postJson("/library_photos/finalize", { community_id: this.communityIdValue })

    const destination = this.returnUrlValue && this.returnUrlValue.length > 0 ? this.returnUrlValue : "/discoveries"
    if (window.Turbo) {
      window.Turbo.visit(destination, { action: "replace" })
    } else {
      window.location.href = destination
    }
  }
}
